import React, { FC, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { useProductAppropriatenessTestStatus } from '@pages/ProductRegistration/AppropriatenessTest/hooks/useProductAppropriatenessTestStatus'

import { useSessionEntity } from '@context/EntityContext'

import { Button } from '@ui/components/Button'
import { ContentBlock } from '@ui/components/ContentBlock/ContentBlock'
import { FeatureIcon } from '@ui/components/FeatureIcon/FeatureIcon'
import { CircleCloseIcon } from '@ui/icons/CircleClose.icon'

import { TickmillCompaniesEnum } from '@utils/companyName.utils'
import { CommonPaths } from '@utils/path'

import { TickmillProductType } from '@model/TickmillProductType'

import { useLogout } from '@hooks/useLogout'
import { useScrollToTop } from '@hooks/useScrollToTop'

import { FullScreenFooter } from '@shared/templates/FullScreenLayout'
import { TestTemplate } from '@shared/templates/TestTemplate'

import { useResolvedAppTestProduct } from './hooks/useResolvedAppTestProduct'

export const ProductAppropriatenessTestDeniedPage: FC = () => {
  useScrollToTop()
  const contentRef = useRef<HTMLDivElement>(null)

  const { t } = useTranslation()
  const navigate = useNavigate()
  const resolvedProductId = useResolvedAppTestProduct()
  const { logout } = useLogout()
  const entity = useSessionEntity()
  const isSQFProduct = resolvedProductId === TickmillProductType.SQF

  const data = useProductAppropriatenessTestStatus(resolvedProductId)
  const { apTestAnswers, isLoading } = data
  const isDeniedConfirmed = isSQFProduct || apTestAnswers?.isClientVulnerable === true

  useEffect(() => {
    if (isSQFProduct) {
      return
    }

    if (isLoading) {
      return
    }

    if (!isDeniedConfirmed) {
      navigate('../failed', { replace: true })
      return
    }

    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href)
      logout()
      navigate('/logout')
    }

    window.history.pushState(null, '', window.location.href)
    window.onpopstate = handlePopState

    return () => {
      window.onpopstate = null
    }
  }, [isDeniedConfirmed, isLoading, isSQFProduct, logout, navigate])

  const clearSessionAndLogout = () => {
    logout()
    navigate('/logout')
  }

  const isEU = entity === TickmillCompaniesEnum.TICKMILL_EU
  const title = isSQFProduct
    ? t('SQF.Not Suitable')
    : isEU
    ? t('Application Denied')
    : t('Sign up.Your registration has been unsuccessful') + '!'
  const description = isSQFProduct
    ? [t('SQF.result.failed')]
    : isEU
    ? [t('Sign up.Based on your responses to the Appropriateness Test')]
    : [
        t('Sign up.From the information you have provided, we do not think', {
          email: 'support@tickmill.co.uk',
        }),
      ]

  return (
    <TestTemplate
      maxWidth={600}
      isLoading={isLoading || !isDeniedConfirmed}
      contentRef={contentRef}
      noHeader
    >
      {isDeniedConfirmed && (
        <>
          <ContentBlock
            variant='voucher'
            icon={
              <FeatureIcon
                size='lg'
                icon={CircleCloseIcon}
                bg='surface-danger-high-em'
                color='text-white'
              />
            }
            title={title}
            description={description}
          />
          <FullScreenFooter contentRef={contentRef}>
            <Button.Primary
              fullWidth
              size='lg'
              onClick={
                isSQFProduct
                  ? () => navigate(CommonPaths.TradersRoomWallets)
                  : clearSessionAndLogout
              }
            >
              {isSQFProduct ? t('Close') : t('Profile.Logout')}
            </Button.Primary>
          </FullScreenFooter>
        </>
      )}
    </TestTemplate>
  )
}
